import React from "react";

/**
 * Constellation stratégique™ — visualisation symbolique en 6 axes.
 * Aucun chiffre, aucune échelle, aucun pourcentage.
 * Les points lumineux sont purement décoratifs — ils ne représentent aucune donnée quantifiée.
 * Rendu bronze/cuivre sur fond noir, cohérent avec l'identité Maison Beaudette™.
 */
const AXES = [
  "Forces Structurantes™",
  "Alignement d'exécution",
  "Leviers de Croissance™",
  "Maturité stratégique",
  "Angles Morts™",
  "Clarté de vision",
];

// Positions décoratives des points sur chaque axe (0 = centre, 1 = extrémité).
// Choisies pour créer une composition organique et harmonieuse, sans lien avec les réponses.
const DOT_POS = [0.72, 0.58, 0.82, 0.66, 0.5, 0.76];

const SIZE = 520;
const PAD_X = 200;  // marge horizontale généreuse pour laisser respirer les libellés latéraux
const PAD_Y = 40;
const VIEW_W = SIZE + PAD_X * 2;
const VIEW_H = SIZE + PAD_Y * 2;
const CENTER_X = VIEW_W / 2;
const CENTER_Y = VIEW_H / 2;
const CENTER = CENTER_X; // compat avec les anciens usages verticaux
const R_MAX = 200;
const RINGS = [0.28, 0.5, 0.72, 0.92];

const axisPoint = (i, r) => {
  const angle = (-Math.PI / 2) + i * (Math.PI / 3); // 6 axes, premier vers le haut
  return { x: CENTER_X + Math.cos(angle) * r, y: CENTER_Y + Math.sin(angle) * r };
};

const hexPath = (ratio) => {
  const pts = Array.from({ length: 6 }, (_, i) => axisPoint(i, R_MAX * ratio));
  return "M " + pts.map((p) => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" L ") + " Z";
};

const labelPosition = (i) => {
  // Positions décollées du dernier anneau pour laisser respirer chaque libellé
  const angle = (-Math.PI / 2) + i * (Math.PI / 3);
  const r = R_MAX * 1.22;
  const p = { x: CENTER_X + Math.cos(angle) * r, y: CENTER_Y + Math.sin(angle) * r };
  let anchor = "middle";
  const cos = Math.cos(angle);
  if (cos > 0.35) anchor = "start";
  else if (cos < -0.35) anchor = "end";
  return { ...p, anchor };
};

export default function Constellation() {
  return (
    <div className="w-full flex justify-center" data-testid="constellation-strategique">
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        role="img"
        aria-label="Constellation stratégique — visualisation symbolique des six dimensions de la Méthode MB™."
        className="w-full max-w-[720px] h-auto"
      >
        <defs>
          <radialGradient id="glow-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#C49361" stopOpacity="0.35" />
            <stop offset="60%" stopColor="#A67C52" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#000000" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="glow-dot" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#E9C79A" stopOpacity="1" />
            <stop offset="55%" stopColor="#C49361" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#A67C52" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Halo central */}
        <circle cx={CENTER_X} cy={CENTER_Y} r={R_MAX * 1.1} fill="url(#glow-core)" />

        {/* Anneaux hexagonaux concentriques */}
        {RINGS.map((r) => (
          <path
            key={`ring-${r}`}
            d={hexPath(r)}
            fill="none"
            stroke="#A67C52"
            strokeWidth="0.8"
            opacity={0.18 + RINGS.indexOf(r) * 0.06}
          />
        ))}

        {/* Axes radiaux */}
        {AXES.map((label, i) => {
          const end = axisPoint(i, R_MAX * 0.98);
          return (
            <line
              key={`axis-${label}`}
              x1={CENTER_X}
              y1={CENTER_Y}
              x2={end.x}
              y2={end.y}
              stroke="#A67C52"
              strokeWidth="0.7"
              opacity="0.42"
            />
          );
        })}

        {/* Cercle central discret */}
        <circle cx={CENTER_X} cy={CENTER_Y} r="3" fill="#C49361" opacity="0.9" />

        {/* Points lumineux — décoratifs, sans échelle ni valeur */}
        {AXES.map((_, i) => {
          const p = axisPoint(i, R_MAX * DOT_POS[i]);
          const pulseClass =
            i % 3 === 0 ? "constellation-dot" :
            i % 3 === 1 ? "constellation-dot constellation-dot--slow" :
            "constellation-dot constellation-dot--slower";
          return (
            <g key={`dot-${i}`} className={pulseClass}>
              <circle cx={p.x} cy={p.y} r="14" fill="url(#glow-dot)" />
              <circle cx={p.x} cy={p.y} r="3.5" fill="#F3D9A9" />
            </g>
          );
        })}

        {/* Libellés des 6 dimensions */}
        {AXES.map((label, i) => {
          const l = labelPosition(i);
          return (
            <text
              key={`lbl-${i}`}
              x={l.x}
              y={l.y}
              className="constellation-label"
              textAnchor={l.anchor}
              dominantBaseline="middle"
            >
              {label}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
